import { useRef, useEffect } from 'react';
import styled from 'libs/styled';
import { above } from '@sportson/core-web/utils/mediaqueries';
import decodeHTMLEntities from '@sportson/core-web/utils/decodeHTMLEntities';

import Wysiwyg from 'libs/wordpress/components/Wysiwyg';
import Text from 'components/Text';
import { inServer } from '@sportson/core-web/constants';

const ContentWrapper = styled('div')`
    display: flex;
`;

const ImageWrapper = styled('div')`
    display: flex;
    justify-content: center;
    align-items: flex-start;
    border-radius: 4px;
    aspect-ratio: 3/2;
    overflow: hidden;
    margin-bottom: auto;

    ${above['desktop.sm']} {
        min-width: 50%;
        max-width: 50%;
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const TextWrapper = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    padding-right: 40px;
`;

// TODO: NOTE: Remove 'extends Record<String, unknown> when style system is properly typed
// TODO: NOTE: Add proper types to component
export interface ArticlePageHeroProps extends Record<string, unknown> {
    image: ArticleImage;
    tag: string;
    title: any;
    summary: string[];
}

// TODO: NOTE: Fix proper type
export interface ArticleImage {
    alt: any;
    src: any;
}

export const ArticlePageHero = ({ image, tag, title, summary, ...rest }: ArticlePageHeroProps) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const headerHeight =
        (!inServer && parseInt(getComputedStyle(document?.documentElement).getPropertyValue('--header-height'))) || 0;

    const scrollToTop = () => {
        // Scroll back to top of wrapper on pagination
        if (wrapperRef && wrapperRef.current) {
            scrollTo({
                top: wrapperRef.current.offsetTop - headerHeight,
            });
        }
    };

    useEffect(() => {
        scrollToTop();
    }, []);

    return (
        <ContentWrapper
            flexDirection={['column', null, null, null, null, 'row']}
            gap={['24px', null, null, null, null, '40px']}
            p={['24px 12px', null, '24px 16px', null, null, '40px']}
            {...rest}
            ref={wrapperRef}
        >
            <ImageWrapper>
                {/* TODO: NOTE: Implement proper image component */}
                {image?.src && <img src={image.src} alt={image.alt} />}
            </ImageWrapper>

            <TextWrapper maxWidth={['100%', null, null, null, '50%']}>
                {tag && (
                    <Text typography="UI/12_100_0_450_uppercase" color="var(--color-neutrals-500)">
                        {tag}
                    </Text>
                )}

                {title && (
                    <Wysiwyg
                        data={decodeHTMLEntities(title?.text || title)}
                        TextComponent={(props) => (
                            <Text
                                as={title?.type || 'h2'}
                                color={title?.color || 'var(--text-color-default)'}
                                typography={['Content/24_130_0_450', null, null, null, 'Content/48_120_0_500']}
                                {...props}
                            />
                        )}
                    />
                )}

                {summary && (
                    <Wysiwyg
                        data={summary}
                        TextComponent={(props) => <Text typography="Content/20_140_0_350" as="p" {...props} />}
                    />
                )}
            </TextWrapper>
        </ContentWrapper>
    );
};
